import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Mail, Package } from 'lucide-react';
import { goToHomeSection } from '../lib/hashSectionNav';
import { SITE_IMAGES } from '../constants/publicAssets';
import { SPRINTFIT_COLLECTIONS_URL } from '../constants/storeUrls';
import { TrustBar } from './TrustBar';

const FOOTER_QUICK_SECTIONS = [
  { id: 'signature', label: 'Our signature' },
  { id: 'product-rail', label: 'Shop' },
  { id: 'reviews', label: 'Reviews' },
  { id: 'faq', label: 'FAQ' },
];

export const Footer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  return (
    <footer data-testid="footer" className="relative border-t border-white/[0.06] bg-[#080809]">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-14 md:py-20">
        <p className="font-editorial text-lg md:text-xl text-zinc-400 text-center mb-8 md:mb-10 tracking-wide">
          Shop with the same care we put into the product.
        </p>
        <TrustBar className="mb-14 md:mb-16 max-w-5xl mx-auto" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12 md:gap-14">
          {/* Brand */}
          <div className="space-y-6">
            <Link to="/" className="flex items-center gap-3 group">
              <div
                className="h-14 w-14 md:h-16 md:w-16 shrink-0 skew-x-[-10deg] rounded-md overflow-hidden ring-1 ring-white/10 bg-zinc-950/90 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.05)] flex items-center justify-center p-0.5 group-hover:ring-white/20 transition-all duration-300"
                aria-hidden
              >
                <img
                  src={SITE_IMAGES.sprintFitMark}
                  alt=""
                  className="h-full w-full max-h-[92%] max-w-[92%] object-contain object-center skew-x-[10deg] [filter:brightness(1.1)_saturate(1.08)_contrast(1.04)] drop-shadow-[0_2px_8px_rgba(0,0,0,0.85)]"
                />
              </div>
              <span className="font-heading text-2xl font-black uppercase tracking-tight text-white">
                Sprint<span className="text-teal-400">Fit</span>
              </span>
            </Link>
            <p className="text-zinc-500 text-[11px] font-mono uppercase tracking-[0.2em]">Pro-grade recovery · India</p>
            <p className="text-zinc-400 text-sm leading-relaxed max-w-sm">
              Contrast, EMS, compression, percussion, and rest—one recovery OS, engineered for daily use and shipped pan-India.
            </p>
            <p className="text-zinc-500 text-xs leading-relaxed">
              Shop and secure checkout live on{' '}
              <a
                href="https://shop.sprintfit.in"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#9fd4c9] hover:underline underline-offset-4"
              >
                shop.sprintfit.in
              </a>
              , our official store.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading text-lg font-bold uppercase tracking-wide mb-6">Quick Links</h4>
            <ul className="space-y-4">
              {FOOTER_QUICK_SECTIONS.map(({ id, label }) => (
                <li key={id}>
                  <a
                    href={`/#${id}`}
                    onClick={(e) => {
                      e.preventDefault();
                      goToHomeSection(navigate, location.pathname, id);
                    }}
                    className="text-zinc-400 hover:text-white transition-colors duration-300 text-sm cursor-pointer"
                  >
                    {label}
                  </a>
                </li>
              ))}
              <li>
                <Link to="/science" className="text-zinc-400 hover:text-white transition-colors duration-300 text-sm">
                  Science &amp; guides
                </Link>
              </li>
              <li>
                <a
                  href={SPRINTFIT_COLLECTIONS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-zinc-400 hover:text-white transition-colors duration-300 text-sm"
                >
                  Full catalog
                </a>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-mono text-[11px] uppercase tracking-[0.22em] text-zinc-500 mb-5">Contact</h4>
            <ul className="space-y-4">
              <li>
                <a
                  href="mailto:support@sprintfit.in"
                  className="flex items-center gap-3 text-zinc-400 hover:text-white transition-colors text-sm"
                >
                  <Mail className="w-4 h-4 text-[#8fb8ae] shrink-0" />
                  support@sprintfit.in
                </a>
              </li>
              <li className="flex items-start gap-3 text-zinc-400 text-sm">
                <Package className="w-4 h-4 text-[#8fb8ae] mt-0.5 shrink-0" />
                <span>
                  Ships across India
                  <span className="text-zinc-500 block text-xs mt-1">
                    1-year warranty · 7-day return · SprintFit replies within 1–2 business days
                  </span>
                </span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom */}
        <div className="mt-14 pt-8 border-t border-white/[0.06] flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-zinc-500 text-sm text-center md:text-left">
            © 2026 SprintFit. All rights reserved.
            <span className="text-zinc-600 block text-xs mt-1 md:inline md:mt-0 md:ml-2">ThermaCube is a SprintFit product line.</span>
          </p>
          <div className="flex gap-6 text-sm text-zinc-500">
            <Link to="/privacy" className="hover:text-white transition-colors duration-300">
              Privacy Policy
            </Link>
            <Link to="/terms" className="hover:text-white transition-colors duration-300">
              Terms of Service
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};
